import { getTime, minutesToMilliseconds, secondsToMilliseconds } from "@suse-o11y/date-time";
import type { Time } from "@suse-o11y/date-time/time";
import type { EnforcedKeys } from "@suse-o11y/enforced-keys";
import { isArray } from "@suse-o11y/is-array";
import type { JSON } from "@suse-o11y/json";
import {
	type QueryClient,
	type QueryClientConfig,
	type QueryFunction,
	type QueryKey,
	useInfiniteQuery as RqUseInfiniteQuery,
	type UseInfiniteQueryOptions as RqUseInfiniteQueryOptions,
	useQueries as RqUseQueries,
	useQuery as RqUseQuery,
	type UseQueryOptions as RqUseQueryOptions,
	type UseQueryResult as RqUseQueryResult,
	type UseInfiniteQueryResult,
} from "@suse-o11y/query";
import { useId } from "react";

import type { TimeRange } from "../app/state/view/timeline/TimeRange";
import { useOnTimelineSummaryUpdate } from "../components/react/timeline/use-on-timeline-summary-update";
import { HTTPError } from "../generic/request";
import { type Setter, resolve } from "../generic/setter";

const LIVE_CACHE_TTL = secondsToMilliseconds(15);
const SNAPSHOT_CACHE_TTL = minutesToMilliseconds(5);
const REGULAR_CACHE_TTL = secondsToMilliseconds(30);

export const DEFAULT_QUERY_CLIENT_CONFIG = {
	defaultOptions: {
		queries: {
			staleTime: 0, // the data becomes stale immediately by default
			cacheTime: REGULAR_CACHE_TTL,
			retry: (failureCount, error) => failureCount < 2 && error instanceof HTTPError && error.shouldRetry(),
		},
	},
} satisfies QueryClientConfig;

const REFETCH_ON_TIMELINE_SUMMARY_UPDATE_OPTIONS = {
	cancelRefetch: false,
	fetching: false,
	active: true,
};

export type InvalidateFunction<TArgs extends Array<unknown> = []> = (...args: TArgs) => Promise<void>;

interface UseQueryCustomOptions {
	timestamp?: null | Time;
	timeRange?: null | TimeRange;
	refetchOnTimelineSummaryUpdate?: boolean;

	/**
	 * If true, appends a uniq namespace to the query key, so that the query does not share the cache with same queries.
	 *
	 * @default false
	 */
	namespace?: JSON;
}

export type UseRequiredQueryOptions<TQueryFnData = unknown, TQueryKey extends QueryKey = QueryKey> = Required<
	Pick<RqUseQueryOptions<TQueryFnData, unknown, unknown, TQueryKey>, "queryKey" | "queryFn">
>;

export type UseFullQueryOptions<
	TQueryFnData = unknown,
	TError = unknown,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = QueryKey,
> = UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> & UseRequiredQueryOptions<TQueryFnData, TQueryKey>;

export interface UseQueryOptions<
	TQueryFnData = unknown,
	TError = unknown,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = QueryKey,
> extends UseQueryCustomOptions,
		Omit<RqUseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, "queryKey" | "queryFn"> {}

export interface UseInfiniteQueryOptions<
	TPageParam,
	TQueryFnData = unknown,
	TError = unknown,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = QueryKey,
> extends UseQueryCustomOptions,
		Omit<
			RqUseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey>,
			"queryKey" | "queryFn" | "getNextPageParam" | "getPreviousPageParam"
		> {
	getPreviousPageParam?: (firstPage: TQueryFnData, allPages: Array<TQueryFnData>) => undefined | TPageParam;
	getNextPageParam?: (lastPage: TQueryFnData, allPages: Array<TQueryFnData>) => undefined | TPageParam;
}

type QueryFunctionBaseContext<TQueryKey extends QueryKey = QueryKey> = Parameters<QueryFunction<unknown, TQueryKey>>[0];

export type InfiniteQueryFunctionContext<TPageParam, TQueryKey extends QueryKey = QueryKey> = Omit<
	QueryFunctionBaseContext<TQueryKey>,
	"pageParam"
> & {
	pageParam?: TPageParam;
};

export interface UseRequiredInfiniteQueryOptions<
	TPageParam,
	TQueryFnData = unknown,
	TQueryKey extends QueryKey = QueryKey,
> {
	queryKey: TQueryKey;
	queryFn: (context: InfiniteQueryFunctionContext<TPageParam, TQueryKey>) => Promise<TQueryFnData> | TQueryFnData;
}

export type UseFullInfiniteQueryOptions<
	TPageParam,
	TQueryFnData = unknown,
	TError = unknown,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = QueryKey,
> = UseInfiniteQueryOptions<TPageParam, TQueryFnData, TError, TData, TQueryKey> &
	UseRequiredInfiniteQueryOptions<TPageParam, TQueryFnData, TQueryKey>;

export type UseQueryResult<TData = unknown, TError = unknown> = RqUseQueryResult<TData, TError>;

type UseFullTimedQueryOptions = UseFullQueryOptions | UseFullInfiniteQueryOptions<unknown>;

const applyRefetchOnTimelineSummaryUpdate = <TOptions extends UseFullTimedQueryOptions>(
	options: TOptions,
): TOptions => {
	if (options.enabled === false) {
		return { ...options, refetchOnTimelineSummaryUpdate: false };
	}

	if (!options.refetchOnTimelineSummaryUpdate) {
		return options;
	}

	// disable auto-refetch
	return {
		...options,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
	};
};

export const applyTimestamps = <TOptions extends UseFullTimedQueryOptions>(
	uniqNamespace: string,
	{ queryKey, namespace = false, ...options }: TOptions,
): Omit<TOptions, "namespace" | "timestamp" | "timeRange"> => {
	const keys = isArray(queryKey) ? queryKey : [queryKey];
	const metaKey = namespace
		? {
				namespace: namespace === true ? uniqNamespace : namespace,
			}
		: {};

	// neither timestamp nor timeRange are passing via the options
	if (!("timestamp" in options || "timeRange" in options)) {
		return {
			queryKey: [metaKey, ...keys],
			cacheTime: REGULAR_CACHE_TTL,
			...options,
		} as Omit<TOptions, "namespace" | "timestamp" | "timeRange">;
	}

	const { timestamp, timeRange, ...optionsWithoutTime } = options;

	// it is live when either both timestamp and timeRange are not defined
	// or timeRange is not specific (until now) regardless of timestamp
	if ((timestamp == null && timeRange == null) || timeRange?.isSpecific() === false) {
		return {
			queryKey: [
				{
					...metaKey,
					timestamp: getTime(timestamp),
					timeRange: timeRange?.toString(),
				},
				...keys,
			] as QueryKey,
			cacheTime: LIVE_CACHE_TTL,
			...optionsWithoutTime,
		} as Omit<TOptions, "namespace" | "timestamp" | "timeRange">;
	}

	return {
		queryKey: [
			{
				...metaKey,
				timestamp: getTime(timestamp),
				timeRange: timeRange == null ? undefined : [getTime(timeRange.start()), getTime(timeRange.endDate())],
			},
			...keys,
		] as QueryKey,
		staleTime: Number.POSITIVE_INFINITY,
		cacheTime: SNAPSHOT_CACHE_TTL,
		...optionsWithoutTime,
		// don't let the snapshot queries refetch
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		refetchInterval: false,
		refetchOnTimelineSummaryUpdate: false,
	} as Omit<TOptions, "namespace" | "timestamp" | "timeRange">;
};

const makeOptions = <TOptions extends UseFullTimedQueryOptions>(
	uniqNamespace: string,
	options: TOptions,
): Omit<TOptions, "namespace" | "timestamp" | "timeRange"> =>
	applyRefetchOnTimelineSummaryUpdate(applyTimestamps(uniqNamespace, options));

export function useQuery<TQueryFnData, TError = unknown, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
	options: UseFullQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError>;
export function useQuery<TQueryFnData, TError = unknown, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
	queryKey: TQueryKey,
	queryFn: QueryFunction<TQueryFnData, TQueryKey>,
	options?: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError>;
export function useQuery(
	...args: [options: UseFullQueryOptions] | [queryKey: QueryKey, queryFn: QueryFunction, options?: UseQueryOptions]
): UseQueryResult {
	const fullOptions =
		args.length === 1
			? args[0]
			: {
					queryKey: args[0],
					queryFn: args[1],
					...args[2],
				};

	const { refetchOnTimelineSummaryUpdate, ...transformedOptions } = makeOptions(useId(), fullOptions);
	const result = RqUseQuery(transformedOptions);

	useOnTimelineSummaryUpdate(
		() => {
			void result.refetch(REFETCH_ON_TIMELINE_SUMMARY_UPDATE_OPTIONS);
		},
		{
			enabled: refetchOnTimelineSummaryUpdate,
		},
	);

	return result;
}

export const useInfiniteQuery = <
	TQueryFnData,
	TError = unknown,
	TPageParam = unknown,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = QueryKey,
>(
	options: UseFullInfiniteQueryOptions<TPageParam, TQueryFnData, TError, TData, TQueryKey>,
): UseInfiniteQueryResult<TData, TError> => {
	const { refetchOnTimelineSummaryUpdate, ...transformedOptions } = makeOptions(useId(), options);
	const { queryFn, ...restOptions } = transformedOptions;
	const result = RqUseInfiniteQuery({
		...restOptions,
		queryFn: queryFn as QueryFunction<TQueryFnData, TQueryKey>,
	});

	useOnTimelineSummaryUpdate(
		() => {
			void result.refetch(REFETCH_ON_TIMELINE_SUMMARY_UPDATE_OPTIONS);
		},
		{
			enabled: refetchOnTimelineSummaryUpdate,
		},
	);

	if (!result.isError) {
		return result;
	}

	// there is no nextPage if there is an error
	return { ...result, hasNextPage: false };
};

export const useQueries = <TQueryFnData, TError = unknown, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
	queries: undefined | ReadonlyArray<UseFullQueryOptions<TQueryFnData, TError, TData, TQueryKey>>,
): Array<UseQueryResult<TData, TError>> => {
	const uniqNamespace = useId();
	const transformedQueries = queries?.map((options, index) => makeOptions(`${uniqNamespace}@${index}`, options)) ?? [];
	const results = RqUseQueries(transformedQueries);

	useOnTimelineSummaryUpdate(
		() => {
			for (const [index, { refetch }] of results.entries()) {
				const query = transformedQueries.at(index);

				if (query?.refetchOnTimelineSummaryUpdate) {
					void refetch(REFETCH_ON_TIMELINE_SUMMARY_UPDATE_OPTIONS);
				}
			}
		},
		{
			enabled: transformedQueries.some(({ refetchOnTimelineSummaryUpdate }) => refetchOnTimelineSummaryUpdate),
		},
	);

	return results as Array<UseQueryResult<TData, TError>>;
};

export interface QueryCacheInteractions<TData> {
	prefetchData?(this: void, queryClient: QueryClient): Promise<void>;
	fetchData?(this: void, queryClient: QueryClient): Promise<TData>;
	getData?(this: void, queryClient: QueryClient): undefined | TData;
	setData?(this: void, queryClient: QueryClient, data: TData): TData;
	removeData?(this: void, queryClient: QueryClient): void;
	invalidate?(this: void, queryClient: QueryClient): Promise<void>;
	cancel?(this: void, queryClient: QueryClient): Promise<void>;
	reset?(this: void, queryClient: QueryClient): Promise<void>;
}

export interface OptimisticUpdate<TData> {
	invalidateOptimisticData(this: void): Promise<void>;
	cancelOngoingQuery(this: void): Promise<void>;
	rollbackOptimisticUpdates(this: void): TData;
}

export function optimisticQueryUpdate<TData>(
	queryClient: QueryClient,
	{
		getData,
		setData,
		removeData,
		invalidate,
		cancel,
	}: EnforcedKeys<QueryCacheInteractions<TData>, "getData" | "setData" | "removeData" | "invalidate" | "cancel">,
	setter: Setter<TData>,
): undefined | OptimisticUpdate<TData> {
	const data = getData(queryClient);

	if (data == null) {
		return undefined;
	}

	const next = resolve(setter, data);

	if (next == null) {
		removeData(queryClient);
	} else {
		setData(queryClient, resolve(setter, data));
	}

	return {
		invalidateOptimisticData: () => invalidate(queryClient),
		cancelOngoingQuery: () => cancel(queryClient),
		rollbackOptimisticUpdates: () => setData(queryClient, data),
	};
}
